import { Head, usePage } from "@inertiajs/react"
import { useState, useEffect } from "react"
import { router } from "@inertiajs/react"
import {
  FileText,
  Folder,
  Star,
  StarOff,
  GripVertical,
  ArrowUpDown,
  Loader2,
  Check,
} from "lucide-react"
import {
  DropdownMenu,
  DropdownMenuTrigger,
  DropdownMenuContent,
  DropdownMenuItem,
} from "@/components/ui/dropdown-menu"
import {
  DndContext,
  closestCenter,
  KeyboardSensor,
  PointerSensor,
  useSensor,
  useSensors,
  type DragEndEvent,
} from "@dnd-kit/core"
import {
  arrayMove,
  SortableContext,
  sortableKeyboardCoordinates,
  useSortable,
  verticalListSortingStrategy,
} from "@dnd-kit/sortable"
import { CSS } from "@dnd-kit/utilities"

type SortMode = "recently_created" | "recently_updated" | "alphabetical" | "custom"

const SORT_LABELS: Record<SortMode, string> = {
  recently_created: "Recently Created",
  recently_updated: "Recently Updated",
  alphabetical: "Alphabetical",
  custom: "Custom Order",
}

interface FavoriteEntry {
  id: number
  path: string
  item_type: "file" | "folder"
  name: string
  position: number
  clientModified?: string
  serverModified?: string
  created_at: string
}

interface FavoritesProps {
  favoritesSort: SortMode
}

function getCSRFToken(): string {
  const meta = document.querySelector('meta[name="csrf-token"]')
  return meta?.getAttribute("content") || ""
}

function SortableRow({
  entry,
  isCustom,
  onRemove,
  onClick,
}: {
  entry: FavoriteEntry
  isCustom: boolean
  onRemove: (path: string) => void
  onClick: (entry: FavoriteEntry) => void
}) {
  const { attributes, listeners, setNodeRef, transform, transition, isDragging } = useSortable({
    id: entry.id,
    disabled: !isCustom,
  })

  const style = {
    transform: CSS.Transform.toString(transform),
    transition,
    zIndex: isDragging ? 10 : undefined,
    opacity: isDragging ? 0.5 : 1,
  }

  return (
    <div
      ref={setNodeRef}
      style={style}
      className="group flex items-center gap-3 px-4 py-3 hover:bg-accent/50 cursor-pointer bg-card"
      onClick={() => onClick(entry)}
    >
      {isCustom && (
        <button
          className="cursor-grab active:cursor-grabbing touch-none text-muted-foreground hover:text-foreground shrink-0"
          {...attributes}
          {...listeners}
          onClick={(e) => e.stopPropagation()}
        >
          <GripVertical className="h-4 w-4" />
        </button>
      )}
      {entry.item_type === "folder" ? (
        <Folder className="h-5 w-5 text-amber-500 shrink-0" />
      ) : (
        <FileText className="h-5 w-5 text-muted-foreground shrink-0" />
      )}
      <div className="flex-1 min-w-0">
        <p className="truncate text-sm font-medium">{entry.name}</p>
      </div>
      <button
        onClick={(e) => {
          e.stopPropagation()
          onRemove(entry.path)
        }}
        className="rounded-md p-1 text-muted-foreground/50 hover:text-destructive hover:bg-destructive/10 transition-colors"
        title="Remove from Favorites"
      >
        <StarOff className="h-4 w-4" />
      </button>
    </div>
  )
}

export default function Favorites() {
  const { favoritesSort: initialSort } = usePage().props as unknown as FavoritesProps
  const [favorites, setFavorites] = useState<FavoriteEntry[]>([])
  const [loading, setLoading] = useState(true)
  const [sortMode, setSortMode] = useState<SortMode>(initialSort)

  const sensors = useSensors(
    useSensor(PointerSensor, { activationConstraint: { distance: 5 } }),
    useSensor(KeyboardSensor, { coordinateGetter: sortableKeyboardCoordinates })
  )

  useEffect(() => {
    loadFavorites()
  }, [])

  async function loadFavorites() {
    setLoading(true)
    try {
      const res = await fetch("/api/favorites", {
        headers: { "X-CSRF-Token": getCSRFToken() },
      })
      const data = await res.json()
      setFavorites(data.favorites || [])
    } catch {
      // ignore
    } finally {
      setLoading(false)
    }
  }

  async function handleRemove(path: string) {
    try {
      await fetch("/api/favorites", {
        method: "DELETE",
        headers: { "Content-Type": "application/json", "X-CSRF-Token": getCSRFToken() },
        body: JSON.stringify({ path }),
      })
      setFavorites((prev) => prev.filter((f) => f.path !== path))
    } catch {
      // ignore
    }
  }

  async function handleSortChange(mode: SortMode) {
    setSortMode(mode)
    try {
      await fetch("/api/user_settings", {
        method: "PATCH",
        headers: { "Content-Type": "application/json", "X-CSRF-Token": getCSRFToken() },
        body: JSON.stringify({ favorites_sort: mode }),
      })
    } catch {
      // ignore
    }
  }

  function handleClick(entry: FavoriteEntry) {
    if (entry.item_type === "folder") {
      const folderPath = entry.path.replace(/^\/+/, "")
      const encoded = folderPath.split("/").map((s) => encodeURIComponent(s)).join("/")
      router.visit(`/files/${encoded}`)
    } else {
      const editorPath = entry.path.replace(/^\/+/, "")
      const encoded = editorPath.split("/").map((s) => encodeURIComponent(s)).join("/")
      router.visit(`/files/${encoded}`)
    }
  }

  async function handleDragEnd(event: DragEndEvent) {
    const { active, over } = event
    if (!over || active.id === over.id) return

    const oldIndex = sortedFavorites.findIndex((f) => f.id === active.id)
    const newIndex = sortedFavorites.findIndex((f) => f.id === over.id)
    const reordered = arrayMove(sortedFavorites, oldIndex, newIndex)

    // Update local state immediately
    const withPositions = reordered.map((f, i) => ({ ...f, position: i + 1 }))
    setFavorites(withPositions)

    // Persist to server
    try {
      await fetch("/api/favorites/reorder", {
        method: "POST",
        headers: { "Content-Type": "application/json", "X-CSRF-Token": getCSRFToken() },
        body: JSON.stringify({
          items: withPositions.map((f) => ({ id: f.id, position: f.position })),
        }),
      })
    } catch {
      // ignore
    }
  }

  function getSortedFavorites(): FavoriteEntry[] {
    const copy = [...favorites]
    switch (sortMode) {
      case "recently_created":
        return copy.sort((a, b) => {
          const aDate = a.clientModified || a.created_at || ""
          const bDate = b.clientModified || b.created_at || ""
          return bDate.localeCompare(aDate)
        })
      case "recently_updated":
        return copy.sort((a, b) => {
          const aDate = a.serverModified || a.created_at || ""
          const bDate = b.serverModified || b.created_at || ""
          return bDate.localeCompare(aDate)
        })
      case "alphabetical":
        return copy.sort((a, b) => a.name.localeCompare(b.name))
      case "custom":
        return copy.sort((a, b) => (a.position || 0) - (b.position || 0))
    }
  }

  const sortedFavorites = getSortedFavorites()
  const isCustom = sortMode === "custom"

  return (
    <>
      <Head title="Favorites" />
      <div className="mx-auto max-w-3xl lg:max-w-5xl p-6">
        {/* Toolbar */}
        <div className="flex items-center justify-between gap-4 mb-4">
          <h1 className="text-sm font-medium text-foreground">Favorites</h1>

          <div className="flex items-center gap-1.5 shrink-0">
            {/* Sort dropdown */}
            <DropdownMenu>
              <DropdownMenuTrigger className="flex items-center gap-1.5 h-7 px-2.5 rounded-md text-xs font-medium text-muted-foreground hover:text-foreground hover:bg-accent transition-colors cursor-pointer">
                <ArrowUpDown className="h-3.5 w-3.5" />
                {SORT_LABELS[sortMode]}
              </DropdownMenuTrigger>
              <DropdownMenuContent align="end">
                {(Object.keys(SORT_LABELS) as SortMode[]).map((mode) => (
                  <DropdownMenuItem key={mode} onClick={() => handleSortChange(mode)}>
                    <span className={sortMode === mode ? "font-medium" : ""}>{SORT_LABELS[mode]}</span>
                    {sortMode === mode && <Check className="h-3 w-3 ml-auto" />}
                  </DropdownMenuItem>
                ))}
              </DropdownMenuContent>
            </DropdownMenu>
          </div>
        </div>

        {loading ? (
          <div className="flex items-center justify-center py-24">
            <Loader2 className="h-8 w-8 animate-spin text-muted-foreground" />
          </div>
        ) : sortedFavorites.length === 0 ? (
          <div className="py-12 text-center text-sm text-muted-foreground">
            <Star className="h-8 w-8 mx-auto mb-3 text-muted-foreground/50" />
            <p>No favorites yet.</p>
            <p className="mt-1 text-xs">Use the context menu on any file or folder to add it to your favorites.</p>
          </div>
        ) : isCustom ? (
          <DndContext sensors={sensors} collisionDetection={closestCenter} onDragEnd={handleDragEnd}>
            <SortableContext items={sortedFavorites.map((f) => f.id)} strategy={verticalListSortingStrategy}>
              <div className="rounded-lg border bg-card shadow-xs overflow-hidden divide-y">
                {sortedFavorites.map((entry) => (
                  <SortableRow
                    key={entry.id}
                    entry={entry}
                    isCustom={isCustom}
                    onRemove={handleRemove}
                    onClick={handleClick}
                  />
                ))}
              </div>
            </SortableContext>
          </DndContext>
        ) : (
          <div className="rounded-lg border bg-card shadow-xs overflow-hidden divide-y">
            {sortedFavorites.map((entry) => (
              <div
                key={entry.id}
                className="group flex items-center gap-3 px-4 py-3 hover:bg-accent/50 cursor-pointer"
                onClick={() => handleClick(entry)}
              >
                {entry.item_type === "folder" ? (
                  <Folder className="h-5 w-5 text-amber-500 shrink-0" />
                ) : (
                  <FileText className="h-5 w-5 text-muted-foreground shrink-0" />
                )}
                <div className="flex-1 min-w-0">
                  <p className="truncate text-sm font-medium">{entry.name}</p>
                </div>
                <button
                  onClick={(e) => {
                    e.stopPropagation()
                    handleRemove(entry.path)
                  }}
                  className="rounded-md p-1 text-muted-foreground/50 hover:text-destructive hover:bg-destructive/10 transition-colors"
                  title="Remove from Favorites"
                >
                  <StarOff className="h-4 w-4" />
                </button>
              </div>
            ))}
          </div>
        )}
      </div>
    </>
  )
}
